import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { AscendLogo, Icon } from '@ascend/shared/components';
import { useAuth } from '../contexts/AuthContext';

function OktaIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" fill="currentColor" />
    </svg>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function MicrosoftIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

type SSOProvider = 'okta' | 'google' | 'microsoft';

export function LoginPage() {
  const navigate = useNavigate();
  const { loginWithSSO, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState<SSOProvider | null>(null);
  const [error, setError] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSSO(provider: SSOProvider) {
    setError('');
    setLoading(provider);
    try {
      const ok = await loginWithSSO(provider);
      if (ok) navigate('/dashboard', { replace: true });
    } catch {
      setError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} sign-in failed. Please try again.`);
    } finally {
      setLoading(null);
    }
  }

  const ssoProviders: { id: SSOProvider; label: string; icon: React.ReactNode }[] = [
    { id: 'okta', label: 'Continue with Okta', icon: <OktaIcon className="h-5 w-5" /> },
    { id: 'google', label: 'Continue with Google', icon: <GoogleIcon className="h-5 w-5" /> },
    { id: 'microsoft', label: 'Continue with Microsoft', icon: <MicrosoftIcon className="h-5 w-5" /> },
  ];

  return (
    <div className="flex min-h-screen bg-background">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-primary/5 border-r border-border">
        <div className="max-w-md px-12 space-y-6">
          <AscendLogo size={48} className="text-primary" />
          <h2 className="text-3xl font-bold tracking-tight">Ascend Admin</h2>
          <p className="text-muted-foreground leading-relaxed">
            Manage credit facilities, monitor margin accounts, oversee compliance, and
            administer the Ascend protocol - all from one place.
          </p>
          <div className="flex items-center gap-3 pt-4">
            <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              Internal Use Only
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Icon name="streamline:interface-security-shield-1-shield-protection-security-defend-crime-war-cover" className="h-3.5 w-3.5" />
              SSO Protected
            </span>
          </div>
        </div>
      </div>

      {/* Right login form */}
      <div className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden flex items-center gap-3 mb-2">
            <AscendLogo size={32} className="text-primary" />
            <h2 className="text-xl font-semibold tracking-tight">Ascend Admin</h2>
          </div>

          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Use your organization&apos;s SSO to access the admin panel.
            </p>
          </div>

          {error && (
            <p className="text-sm text-red-500 font-medium">{error}</p>
          )}

          <div className="space-y-3">
            {ssoProviders.map((provider) => (
              <button
                key={provider.id}
                type="button"
                onClick={() => handleSSO(provider.id)}
                disabled={loading !== null}
                className="flex w-full items-center gap-3 rounded-lg border border-border px-4 py-3 text-sm font-medium hover:bg-accent disabled:opacity-60 transition-colors"
              >
                {loading === provider.id ? (
                  <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="h-5 w-5 animate-spin" />
                ) : (
                  provider.icon
                )}
                {loading === provider.id ? 'Redirecting...' : provider.label}
              </button>
            ))}
          </div>

          <p className="text-center text-xs text-muted-foreground">
            Click any provider to sign in. This is a demo environment.
          </p>
        </div>
      </div>
    </div>
  );
}
